/**
 * "Calls & notes" for a conversation (TRUS-668 v5, calls_contract_v5.md §3b).
 *
 * A Calls button in the conversation header (staff only, calls enabled) opens
 * a side panel listing the conversation's recorded calls, newest first — mode,
 * date / time, duration, who started it, notes status — with ready rows
 * expanding to the notes card. "Load more" pages with `next_before`.
 *
 * A search box searches summaries, action items, decisions and transcripts,
 * in This conversation or All conversations; a result shows its conversation
 * and the matched snippet with the search text highlighted. A result in
 * another conversation opens that conversation with its panel on that call
 * (callStore.openCallNotes → the workspace's OPEN_CALL_CHANNEL_EVENT).
 *
 * Members never see any of it, and the member client never calls GET /notes.
 */

import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  ArrowUpRight,
  ChevronDown,
  ChevronRight,
  History,
  Loader2,
  Phone,
  Search,
  Video as VideoIcon,
} from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import useActiveOrgId from '@/hooks/useActiveOrgId';
import { cn } from '@/lib/utils';
import {
  CallsApiError,
  formatCallDuration,
  isNotesPending,
  normalizeNotesQuery,
  parseCallTime,
  type CallNotesListItem,
  type CallNotesMatchField,
} from '@/services/callsApi';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../../ui/sheet';
import { CallNotesCard } from './CallNotesCard';
import { callNotesReasonText, callNotesRecordingGoneText } from './callNotesReason';
import { CallNotesRetryButton } from './CallNotesRetryButton';
import { callStore, useCallStore } from './callStore';
import { useCallsApi, useCallsConfig } from './useCalls';

export const CALL_NOTES_PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

type Scope = 'channel' | 'all';
type TFn = (key: string, fallback?: string, opts?: Record<string, unknown>) => string;

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** The snippet with every case-insensitive occurrence of `query` in a <mark>. */
export function HighlightedSnippet({ text, query }: { text: string; query: string | null }) {
  const parts = useMemo(() => {
    if (!query) return [text];
    return text.split(new RegExp(`(${escapeRegExp(query)})`, 'gi'));
  }, [text, query]);
  const q = query?.toLowerCase();
  return (
    <>
      {parts.map((part, i) =>
        q && part.toLowerCase() === q ? (
          <mark key={i} className="rounded-sm bg-amber-200 px-0.5 text-inherit dark:bg-amber-500/40">
            {part}
          </mark>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        ),
      )}
    </>
  );
}

function formatCallDate(iso: string | null): string {
  const ms = parseCallTime(iso);
  if (ms == null) return '';
  try {
    return new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return new Date(ms).toLocaleString();
  }
}

function statusLabel(t: TFn, item: CallNotesListItem): string {
  if (item.notes_status === 'ready') return t('messaging.calls.notesReady', 'Notes ready');
  // TRUS-708: pending, but waiting for the worker's keys - not "being prepared".
  if (item.notes_reason === 'waiting_for_configuration') return callNotesReasonText(t, item.notes_reason)!.label;
  if (isNotesPending(item.notes_status)) return t('messaging.calls.notesPreparing', 'Notes are being prepared…');
  // Say why there are none: a call still in progress, a failure and its reason…
  const why = callNotesReasonText(t, item.notes_reason ?? (item.notes_status === 'failed' ? 'failed' : null), item.notes_error);
  if (why) return why.label;
  return t('messaging.calls.noNotes', 'No notes');
}

function matchLabel(t: TFn, field: CallNotesMatchField): string {
  switch (field) {
    case 'summary':
      return t('messaging.calls.matchSummary', 'In the summary');
    case 'action_items':
      return t('messaging.calls.matchActionItems', 'In the action items');
    case 'decisions':
      return t('messaging.calls.matchDecisions', 'In the decisions');
    default:
      return t('messaging.calls.matchTranscript', 'In the transcript');
  }
}

interface CallNotesRowProps {
  item: CallNotesListItem;
  currentChannelId: number;
  query: string | null;
  showChannel: boolean;
  expanded: boolean;
  onToggle: () => void;
  onOpenElsewhere: () => void;
}

function CallNotesRow({ item, currentChannelId, query, showChannel, expanded, onToggle, onOpenElsewhere }: CallNotesRowProps) {
  const { t } = useAppTranslation();
  const regionId = useId();
  const rowRef = useRef<HTMLLIElement>(null);
  const elsewhere = item.channel_id !== currentChannelId;
  const ready = item.notes_status === 'ready';
  const Icon = item.mode === 'audio' ? Phone : VideoIcon;
  const modeLabel = item.mode === 'audio' ? t('messaging.calls.audioCall', 'Audio call') : t('messaging.calls.videoCall', 'Video call');
  const when = formatCallDate(item.started_at);
  const duration = item.duration_seconds != null && item.duration_seconds > 0 ? formatCallDuration(item.duration_seconds) : '';
  const channelLabel = item.channel_name || t('messaging.calls.aConversation', 'A conversation');

  useEffect(() => {
    if (expanded) rowRef.current?.scrollIntoView?.({ block: 'nearest' });
    // Only when it first expands (a focused call from another conversation).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const body = (
    <>
      <span
        className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground"
        aria-hidden="true"
      >
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-baseline gap-x-1 text-sm font-medium">
          <span>{modeLabel}</span>
          {when && <span className="font-normal text-muted-foreground">{`· ${when}`}</span>}
        </span>
        {showChannel && (
          <span className="block truncate text-xs font-medium text-foreground" data-testid="call-notes-row-channel">
            {item.is_dm
              ? t('messaging.calls.inDm', 'Direct message · {{name}}', { name: channelLabel })
              : t('messaging.calls.inChannel', 'In {{name}}', { name: channelLabel })}
          </span>
        )}
        <span className="block text-xs text-muted-foreground">
          {[
            duration,
            item.started_by?.name ? t('messaging.calls.startedByName', 'Started by {{name}}', { name: item.started_by.name }) : '',
            statusLabel(t, item),
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
        {item.match && (
          <span className="mt-1 block text-xs" data-testid="call-notes-row-snippet">
            <span className="text-muted-foreground">{`${matchLabel(t, item.match.field)}: `}</span>
            <HighlightedSnippet text={item.match.snippet} query={query} />
          </span>
        )}
      </span>
    </>
  );

  return (
    <li ref={rowRef} className="rounded-md border bg-card" data-testid={`call-notes-row-${item.call_id}`}>
      {elsewhere ? (
        <button
          type="button"
          className="flex w-full items-start gap-3 rounded-md p-3 text-left hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={onOpenElsewhere}
          aria-label={t('messaging.calls.openInConversation', 'Open in {{name}}', { name: channelLabel })}
          data-testid={`call-notes-row-open-${item.call_id}`}
        >
          {body}
          <ArrowUpRight className="mt-1 h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
        </button>
      ) : ready ? (
        <button
          type="button"
          className="flex w-full items-start gap-3 rounded-md p-3 text-left hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-controls={regionId}
          data-testid={`call-notes-row-toggle-${item.call_id}`}
        >
          {body}
          {expanded ? (
            <ChevronDown className="mt-1 h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
          ) : (
            <ChevronRight className="mt-1 h-4 w-4 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
          )}
        </button>
      ) : (
        <div className="flex items-start gap-3 p-3">{body}</div>
      )}
      {item.notes_status === 'failed' && item.notes_media_deleted && (
        <div className="px-3 pb-2 text-xs italic text-muted-foreground" data-testid={`call-notes-row-recording-deleted-${item.call_id}`}>
          {callNotesRecordingGoneText(t)}
        </div>
      )}
      {item.notes_retryable && <CallNotesRetryButton callId={item.call_id} className="px-3 pb-3 pl-14" />}
      {!elsewhere && ready && expanded && (
        <div id={regionId} className="border-t px-3 pb-3 pt-1" data-testid={`call-notes-row-notes-${item.call_id}`}>
          <CallNotesCard meta={{ call_id: item.call_id, notes_id: null }} showTitle={false} />
        </div>
      )}
    </li>
  );
}

interface CallNotesPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channelId: number;
  channelName?: string;
  /** Expand (and scroll to) this call when the panel opens. */
  focusCallId?: number | null;
  /** TRUS-691 meetings hook: rendered above the search (the conversation's upcoming meetings). */
  extraTop?: React.ReactNode;
}

export function CallNotesPanel({ open, onOpenChange, channelId, channelName, focusCallId = null, extraTop }: CallNotesPanelProps) {
  const { t } = useAppTranslation();
  const api = useCallsApi();
  const orgId = useActiveOrgId();
  const searchId = useId();
  const [scope, setScope] = useState<Scope>('channel');
  const [rawQuery, setRawQuery] = useState('');
  const [query, setQuery] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(() => new Set(focusCallId != null ? [focusCallId] : []));

  useEffect(() => {
    const id = setTimeout(() => setQuery(normalizeNotesQuery(rawQuery)), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [rawQuery]);

  useEffect(() => {
    if (focusCallId != null) setExpanded((prev) => new Set(prev).add(focusCallId));
  }, [focusCallId]);

  const scopedChannel = scope === 'channel' ? channelId : null;
  const list = useInfiniteQuery({
    queryKey: ['call-notes-list', api.kind, orgId, scopedChannel, query],
    queryFn: ({ pageParam }) =>
      api.listNotes({
        channelId: scopedChannel,
        q: query,
        limit: CALL_NOTES_PAGE_SIZE,
        before: pageParam ?? null,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_before ?? undefined,
    enabled: open && api.kind === 'staff',
    staleTime: 30_000,
    retry: false,
  });

  const items = useMemo(() => {
    const seen = new Set<number>();
    const out: CallNotesListItem[] = [];
    for (const page of list.data?.pages ?? []) {
      for (const item of page.items) {
        if (seen.has(item.call_id)) continue;
        seen.add(item.call_id);
        out.push(item);
      }
    }
    return out;
  }, [list.data]);

  const toggle = (callId: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(callId)) next.delete(callId);
      else next.add(callId);
      return next;
    });

  const openElsewhere = (item: CallNotesListItem) => {
    onOpenChange(false);
    callStore.openCallNotes(item.channel_id, item.call_id);
  };

  // A focused call older than the first page: show its notes on their own.
  const focusMissing =
    focusCallId != null && list.isSuccess && !items.some((i) => i.call_id === focusCallId);

  const errorCode = list.error instanceof CallsApiError ? list.error.code : null;
  const errorText =
    errorCode === 'members_cannot_list_notes'
      ? t('messaging.calls.notesListStaffOnly', 'Only staff can see call notes.')
      : errorCode === 'not_found'
        ? t('messaging.calls.notesListNotFound', "You can't see this conversation's calls.")
        : errorCode === 'invalid_query'
          ? t('messaging.calls.notesListBadQuery', 'Search for 2 to 200 characters.')
          : t('messaging.calls.notesListFailed', "Couldn't load calls. Try again.");
  // Load more failed (e.g. 422 invalid_cursor): keep the rows, offer a fresh start.
  const moreFailed = list.isFetchNextPageError && items.length > 0;

  const scopeButton = (value: Scope, label: string) => (
    <Button
      type="button"
      size="sm"
      variant={scope === value ? 'secondary' : 'ghost'}
      className="h-7 px-2 text-xs"
      aria-pressed={scope === value}
      onClick={() => setScope(value)}
      data-testid={`call-notes-scope-${value}`}
    >
      {label}
    </Button>
  );

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full gap-0 p-0 sm:max-w-md" data-testid="call-notes-panel">
        <SheetHeader className="border-b pr-10">
          <SheetTitle>{t('messaging.calls.callsAndNotes', 'Calls & notes')}</SheetTitle>
          <SheetDescription>
            {channelName
              ? t('messaging.calls.callsAndNotesIn', 'Recorded calls in {{name}}', { name: channelName })
              : t('messaging.calls.callsAndNotesHint', 'Recorded calls and their notes')}
          </SheetDescription>
        </SheetHeader>

        {extraTop}

        <div className="flex flex-col gap-2 border-b p-3" role="search">
          <label htmlFor={searchId} className="sr-only">
            {t('messaging.calls.searchNotes', 'Search call notes')}
          </label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
            <Input
              id={searchId}
              type="search"
              value={rawQuery}
              onChange={(e) => setRawQuery(e.target.value)}
              placeholder={t('messaging.calls.searchNotesPlaceholder', 'Search summaries and transcripts')}
              className="pl-8"
              maxLength={200}
              data-testid="call-notes-search"
            />
          </div>
          <div
            className="flex items-center gap-1"
            role="group"
            aria-label={t('messaging.calls.searchScope', 'Search in')}
          >
            {scopeButton('channel', t('messaging.calls.scopeThis', 'This conversation'))}
            {scopeButton('all', t('messaging.calls.scopeAll', 'All conversations'))}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-3" aria-busy={list.isFetching} data-testid="call-notes-list">
          {focusMissing && (
            <div className="mb-3 rounded-md border bg-card p-3" data-testid="call-notes-focused">
              <CallNotesCard meta={{ call_id: focusCallId as number, notes_id: null }} />
            </div>
          )}
          {list.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="call-notes-loading">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              {t('messaging.calls.loadingCalls', 'Loading calls…')}
            </div>
          ) : list.isError && !moreFailed ? (
            <div className="flex flex-col items-start gap-2 text-sm text-muted-foreground" role="alert" data-testid="call-notes-error">
              {errorText}
              {errorCode !== 'members_cannot_list_notes' && errorCode !== 'not_found' && errorCode !== 'invalid_query' && (
                <Button type="button" size="sm" variant="outline" onClick={() => void list.refetch()}>
                  {t('messaging.calls.retry', 'Try again')}
                </Button>
              )}
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-8 text-center text-sm text-muted-foreground" data-testid="call-notes-empty">
              <History className="h-6 w-6" aria-hidden="true" />
              {query
                ? t('messaging.calls.noNotesMatch', 'No call notes match “{{query}}”.', { query })
                : scope === 'channel'
                  ? t('messaging.calls.noRecordedCalls', 'No recorded calls in this conversation yet.')
                  : t('messaging.calls.noRecordedCallsAll', 'No recorded calls yet.')}
            </div>
          ) : (
            <>
              <ul className="flex flex-col gap-2" aria-label={t('messaging.calls.callsAndNotes', 'Calls & notes')}>
                {items.map((item) => (
                  <CallNotesRow
                    key={item.call_id}
                    item={item}
                    currentChannelId={channelId}
                    query={query}
                    showChannel={scope === 'all' || item.channel_id !== channelId}
                    expanded={expanded.has(item.call_id)}
                    onToggle={() => toggle(item.call_id)}
                    onOpenElsewhere={() => openElsewhere(item)}
                  />
                ))}
              </ul>
              {moreFailed && (
                <div className="mt-3 flex flex-col items-center gap-2 text-sm text-muted-foreground" role="alert" data-testid="call-notes-more-error">
                  {t('messaging.calls.loadMoreFailed', "Couldn't load more calls.")}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      // A stale cursor never recovers: start the list again from the newest call.
                      errorCode === 'invalid_cursor' ? void list.refetch() : void list.fetchNextPage()
                    }
                    data-testid="call-notes-more-retry"
                  >
                    {t('messaging.calls.retry', 'Try again')}
                  </Button>
                </div>
              )}
              {list.hasNextPage && !moreFailed && (
                <div className="mt-3 flex justify-center">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={list.isFetchingNextPage}
                    onClick={() => void list.fetchNextPage()}
                    data-testid="call-notes-load-more"
                  >
                    {list.isFetchingNextPage && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
                    {t('messaging.calls.loadMore', 'Load more')}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

interface CallNotesButtonProps {
  channelId: number;
  channelName?: string;
  /** A member is viewing: never offer the panel. */
  viewerIsMember?: boolean;
  /** TRUS-691 meetings hook: passed to the panel (upcoming meetings). */
  extraTop?: React.ReactNode;
}

/**
 * The header's Calls button + its panel. Rendered only for staff with calls
 * enabled; also opens itself when a search result in another conversation
 * asked for this one (callStore.openCallNotes).
 */
export function CallNotesButton({ channelId, channelName, viewerIsMember = false, extraTop }: CallNotesButtonProps) {
  const { t } = useAppTranslation();
  const api = useCallsApi();
  const { config } = useCallsConfig();
  const { notesRequest } = useCallStore();
  const [open, setOpen] = useState(false);
  const [focusCallId, setFocusCallId] = useState<number | null>(null);
  const allowed = config.enabled && api.kind === 'staff' && !viewerIsMember;

  // Another conversation: the panel starts closed (declared first, so a
  // search result that opened this conversation re-opens it just below).
  useEffect(() => {
    setOpen(false);
    setFocusCallId(null);
  }, [channelId]);

  useEffect(() => {
    if (!allowed || notesRequest?.channelId !== channelId) return;
    const callId = callStore.takeCallNotesRequest(channelId);
    if (callId != null) {
      setFocusCallId(callId);
      setOpen(true);
    }
  }, [allowed, notesRequest, channelId]);

  if (!allowed) return null;

  const label = t('messaging.calls.callsAndNotes', 'Calls & notes');
  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-1"
        onClick={() => {
          setFocusCallId(null);
          setOpen(true);
        }}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label}
        title={label}
        data-testid="call-notes-button"
      >
        <History className="h-4 w-4" aria-hidden="true" />
        <span className="hidden sm:inline">{t('messaging.calls.callsButton', 'Calls')}</span>
      </Button>
      {open && (
        <CallNotesPanel
          key={`${channelId}:${focusCallId ?? ''}`}
          open={open}
          onOpenChange={setOpen}
          channelId={channelId}
          channelName={channelName}
          focusCallId={focusCallId}
          extraTop={extraTop}
        />
      )}
    </>
  );
}

export default CallNotesPanel;
